import React, { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { AnalyticsTabsLayout } from "@/components/pages/analytics/components/AnalyticsTabsLayout";
import { clearInactiveAnalyticsSubTabParams } from "@/components/pages/analytics/config/analyticsSubTabs";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { OverviewTab } from "./components/OverviewTab";
import { RevenueTab } from "./components/RevenueTab";
import { ServicesTab } from "./components/ServicesTab";
import { OffersTab } from "./components/OffersTab";
import { ReviewsTab } from "./components/ReviewsTab";
import { OperationsTab } from "./components/OperationsTab";
import { TeamTab } from "./components/TeamTab";
import { OverviewFiltersBar } from "./components/overview/OverviewFiltersBar";
import {
  normalizeAnalyticsTab,
  type AnalyticsReportTab,
} from "./components/shared/email-distribution/autoExportMappers";
import {
  ANALYTICS_FD_TABS,
  DEFAULT_ANALYTICS_FD_TAB,
  isAnalyticsFdTab,
  type AnalyticsFdTab,
} from "./config/analyticsFdTabs";
import { isRapidPlan, isStarterPlan } from "./config/planAccess";
import { useAnalyticsPage } from "./context/AnalyticsPageContext";
import { AnalyticsTabExportProvider } from "./context/AnalyticsTabExportContext";

const TAB_PANELS: Record<AnalyticsFdTab, React.ComponentType | undefined> = {
  overview: OverviewTab,
  revenue: RevenueTab,
  services: ServicesTab,
  offers: OffersTab,
  reviews: ReviewsTab,
  operations: OperationsTab,
  team: TeamTab,
};

function FrontDeskAnalyticsDashboardContent() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { dateRange, setDateRange } = useAnalyticsPage();
  const { userProfile } = useAuthenticatedUser();
  const hasRapidPlan = isRapidPlan(userProfile);
  const hasStarterPlan = isStarterPlan(userProfile);
  const availableTabs = useMemo(() => {
    if (hasRapidPlan) {
      return ANALYTICS_FD_TABS.filter((tab) => tab.value === "services").map(
        (tab) => ({ ...tab, labelKey: "tabs.partners" }),
      );
    }

    if (hasStarterPlan) {
      return ANALYTICS_FD_TABS.filter((tab) =>
        ["overview", "services", "reviews"].includes(tab.value),
      );
    }

    return ANALYTICS_FD_TABS;
  }, [hasRapidPlan, hasStarterPlan]);
  const availableTabValues = useMemo(
    () => new Set<AnalyticsFdTab>(availableTabs.map((tab) => tab.value)),
    [availableTabs],
  );
  const availableReportTabs = useMemo<AnalyticsReportTab[]>(
    () => availableTabs.map((tab) => normalizeAnalyticsTab(tab.value)),
    [availableTabs],
  );

  const rawTab = searchParams.get("tab") ?? "";
  let activeTab: AnalyticsFdTab = DEFAULT_ANALYTICS_FD_TAB;
  if (hasRapidPlan) {
    activeTab = "services";
  } else if (isAnalyticsFdTab(rawTab) && availableTabValues.has(rawTab)) {
    activeTab = rawTab;
  }

  useEffect(() => {
    if (!hasRapidPlan && (!hasStarterPlan || rawTab === "")) {
      return;
    }

    if (searchParams.get("tab") === activeTab) {
      return;
    }

    setSearchParams((previousParams) => {
      const nextParams = clearInactiveAnalyticsSubTabParams(
        previousParams,
        activeTab,
      );
      nextParams.set("tab", activeTab);
      return nextParams;
    });
  }, [
    activeTab,
    hasRapidPlan,
    hasStarterPlan,
    rawTab,
    searchParams,
    setSearchParams,
  ]);

  const handleTabChange = (value: string) => {
    if (!isAnalyticsFdTab(value)) {
      return;
    }
    if (!availableTabValues.has(value)) {
      return;
    }

    setSearchParams((previousParams) => {
      const nextParams = clearInactiveAnalyticsSubTabParams(
        previousParams,
        value,
      );
      nextParams.set("tab", value);
      return nextParams;
    });
  };

  return (
    <AnalyticsTabsLayout
      tabs={availableTabs}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      filters={
        <OverviewFiltersBar
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          defaultTab={activeTab}
          availableReportTabs={availableReportTabs}
        />
      }
      renderPanel={(tab) => {
        const Panel = TAB_PANELS[tab];
        return Panel ? <Panel /> : null;
      }}
    />
  );
}

export const FrontDeskAnalyticsDashboard: React.FC = () => {
  return (
    <AnalyticsTabExportProvider>
      <FrontDeskAnalyticsDashboardContent />
    </AnalyticsTabExportProvider>
  );
};
